import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const productImages = [
  { name: 'Midnight Black', hex: '#1A1A1A', image: 'https://customer-assets.emergentagent.com/job_27a04b8e-bf34-46a5-a674-e3b31bbd3a27/artifacts/009zy0w9_fitness%20band%20black.png' },
  { name: 'Cloud White', hex: '#F5F5F5', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/57600vp7_fitness%20band%20white.png' },
  { name: 'Sand Beige', hex: '#E1C699', image: 'https://customer-assets.emergentagent.com/job_27a04b8e-bf34-46a5-a674-e3b31bbd3a27/artifacts/9ax0e27d_Fitness%20band%20bienge.png' },
  { name: 'Sky Blue', hex: '#87CEEB', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/dzn00r33_fitness%20band%20sky%20blue.png' },
  { name: 'Soft Pink', hex: '#FFB6C1', image: 'https://customer-assets.emergentagent.com/job_27a04b8e-bf34-46a5-a674-e3b31bbd3a27/artifacts/3bgjybb4_fitness%20band%20pink.png' },
  { name: 'Ocean Blue', hex: '#0047AB', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/av2155rf_fitness%20band%20blue.png' },
  { name: 'Crimson Red', hex: '#DC143C', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/6xgf8rml_connect%20band%20red.png' },
  { name: 'Mint Green', hex: '#98FB98', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/2t3allwi_fitness%20band%20mint.png' },
];

const ProductShowcase = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextProduct = () => setActiveIndex((prev) => (prev + 1) % productImages.length);
  const prevProduct = () => setActiveIndex((prev) => (prev - 1 + productImages.length) % productImages.length);

  return (
    <section id="product-showcase" className="py-12 md:py-16" data-testid="product-showcase">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-[#1A1A1A] mb-2">
            8 Colors. One Perfect Fit.
          </h2>
          <p className="text-[#5A5A5A] text-sm md:text-base max-w-md mx-auto">
            Lightweight, breathable, and built for all-day comfort.
          </p>
        </motion.div>

        {/* Compact Carousel */}
        <div className="relative max-w-md mx-auto">
          {/* Navigation Buttons */}
          <button
            onClick={prevProduct}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-12
                       w-10 h-10 rounded-full bg-white border border-black/8 shadow-md
                       flex items-center justify-center text-[#6A6A6A] hover:text-[#1A1A1A]
                       hover:shadow-lg transition-all z-10"
            data-testid="carousel-prev-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <button
            onClick={nextProduct}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-12
                       w-10 h-10 rounded-full bg-white border border-black/8 shadow-md
                       flex items-center justify-center text-[#6A6A6A] hover:text-[#1A1A1A]
                       hover:shadow-lg transition-all z-10"
            data-testid="carousel-next-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Product Display - More compact */}
          <motion.div className="relative aspect-[4/3] bg-gradient-to-b from-white/60 to-transparent rounded-2xl overflow-hidden border border-black/5">
            {productImages.map((product, index) => (
              <motion.div
                key={product.name}
                className="absolute inset-0 flex items-center justify-center p-6"
                initial={false}
                animate={{
                  opacity: index === activeIndex ? 1 : 0,
                  scale: index === activeIndex ? 1 : 0.9,
                  x: index === activeIndex ? 0 : index > activeIndex ? 80 : -80,
                }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              >
                <img src={product.image} alt={`Connect Band - ${product.name}`} className="max-w-full max-h-full object-contain product-glow" />
              </motion.div>
            ))}
          </motion.div>

          {/* Product Info */}
          <motion.div key={activeIndex} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mt-4">
            <div 
              className="w-5 h-5 rounded-full mx-auto mb-2 shadow-sm"
              style={{ backgroundColor: productImages[activeIndex].hex, border: productImages[activeIndex].hex === '#F5F5F5' ? '1px solid rgba(0,0,0,0.15)' : 'none' }}
            />
            <h3 className="text-base font-medium text-[#1A1A1A]">{productImages[activeIndex].name}</h3>
          </motion.div>

          {/* Color Dots */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {productImages.map((product, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${index === activeIndex ? 'ring-2 ring-[#1A1A1A]/30 ring-offset-2' : ''}`}
                style={{ backgroundColor: product.hex, border: product.hex === '#F5F5F5' ? '1px solid rgba(0,0,0,0.15)' : 'none' }}
                data-testid={`carousel-dot-${index}`}
              />
            ))}
          </div>
        </div>

        {/* Mini Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-6 mt-8 text-[#6A6A6A] text-sm"
        >
          <span>Breathable strap</span>
          <span>•</span>
          <span>Soft-touch hardware</span>
          <span>•</span>
          <span>All-day comfort</span>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductShowcase;
